import './EditCustomIndicatorModal.css';

import React, { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';
import EditText from '../../b2b-common/EditText';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import { generateSelectField } from '../../b2b-common/helpers';
import { useLoginContext } from '../../../App';
import { createBlankCustomQuantitativeIndicator } from './dtos';
import IndicatorsFieldsFactory from '../components/fields/IndicatorsFieldsFactory';
import { showNotification } from '../../b2b-common/Notification';
import { useContentContext } from '../../layout/Layout';
import { saveNewQualitativeIndicator, saveNewQuantitativeDimensionalIndicator, saveNewQuantitativeIndicator, updateQualitativeIndicator, updateQuantitativeDimensionalIndicator, updateQuantitativeIndicator } from '../../../services/b2b-indicators-apiService';
import NonStandardLevelNs from '../components/frameworks/NonStandardLevelNs';
import Banner from '../../b2b-common/Banner';
import useIndicatorImpact from '../../b2b-frameworks/hooks/useIndicatorImpact';
import { addCustomIndicatorToMultipleLevelNs } from '../../../services/b2b-frameworks-apiService';

const EditCustomIndicatorModal = ({ open, indicator, onClose, onUpdate }) => {

    const { t } = useB2BTranslation('b2b-indicators');
    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();

    const [currentIndicator, setCurrentIndicator] = useState(null);
    const [indicatorUUID, setIndicatorUUID] = useState(null);
    const { indicatorImpact: savedNonStandardLevelNs, clearCache } = useIndicatorImpact('generic', 2, indicatorUUID);
    const [selectedNonStandardLevelNs, setSelectedNonStandardLevelNs] = useState([]);

    // console.log('EditCustomIndicatorModal savedNonStandardLevelNs', savedNonStandardLevelNs);
    // console.log('EditCustomIndicatorModal selectedNonStandardLevelNs', selectedNonStandardLevelNs);

    useEffect(() => {
        if (!indicator) {
            setCurrentIndicator(createBlankCustomQuantitativeIndicator(t, loginData.loggedOrganizationUuid));
        } else {
            if (!isEqual(indicator, currentIndicator)) {
                setCurrentIndicator(indicator);
                setIndicatorUUID(indicator.indicatorUUID);
            }
        }
    }, [indicator]);

    useEffect(() => {
        if (savedNonStandardLevelNs && !isEqual(savedNonStandardLevelNs, selectedNonStandardLevelNs)) {
            setSelectedNonStandardLevelNs(savedNonStandardLevelNs);
        }
    }, [savedNonStandardLevelNs]);

    if (!currentIndicator) return null;

    const handleSaveIndicator = async () => {
        const saveIndicatorFunctions = {
            'QUANTITATIVE': saveNewQuantitativeIndicator,
            'QUALITATIVE': saveNewQualitativeIndicator,
            'QUANTITATIVE_DIMENSIONAL': saveNewQuantitativeDimensionalIndicator,
        };
        const updatedIndicatorFunctions = {
            'QUANTITATIVE': updateQuantitativeIndicator,
            'QUALITATIVE': updateQualitativeIndicator,
            'QUANTITATIVE_DIMENSIONAL': updateQuantitativeDimensionalIndicator,
        };
        if (!currentIndicator.indicatorUUID) { // New indicator
            try {
                const saveFunction = saveIndicatorFunctions[currentIndicator.indicatorType];
                if (saveFunction) {
                    const newInd = await saveFunction(currentIndicator, loginData.accessToken);
                    const newIndicatorUUID = newInd.indicatorUUID;
                    const updatedSelectedNonStandardLevelNs = selectedNonStandardLevelNs.map((item) => ({
                        frameworkId: item.frameworkId,
                        levelN_ItemId: item.levelN_ItemId,
                        indicatorUUID: newIndicatorUUID,
                        organizationUUID: loginData.loggedOrganizationUuid,
                    }));
                    // console.log('to save selectedNonStandardLevelNs', updatedSelectedNonStandardLevelNs);
                    await addCustomIndicatorToMultipleLevelNs('generic', 2, newIndicatorUUID, updatedSelectedNonStandardLevelNs, loginData.accessToken);
                    clearCache();
                    showNotification(setNotification, t('Indicator saved'), 'success', 3000);
                    onUpdate(newInd);
                    onClose();
                } else {
                    throw new Error(t('Unsupported indicator type'));
                }
            } catch (error) {
                console.error('Error saving indicator', error);
                showNotification(setNotification, t('Error saving indicator - ') + error, 'error', 5000);
            }
        } else { // Update indicator
            try {
                const updateFunction = updatedIndicatorFunctions[currentIndicator.indicatorType];
                if (updateFunction) {
                    const updatedInd = await updateFunction(currentIndicator, loginData.accessToken);
                    // console.log('to update selectedNonStandardLevelNs', selectedNonStandardLevelNs);
                    await addCustomIndicatorToMultipleLevelNs('generic', 2, currentIndicator.indicatorUUID, selectedNonStandardLevelNs, loginData.accessToken);
                    clearCache();
                    showNotification(setNotification, t('Indicator updated'), 'success', 3000);
                    onUpdate(updatedInd);
                    onClose();
                } else {
                    throw new Error(t('Unsupported indicator type'));
                }
            } catch (error) {
                console.error('Error updating indicator: ', error);
                showNotification(setNotification, t('Error updating indicator - ') + error, 'error', 5000);
            }
        }
    }

    const handleUpdatedNonStandardLevelNs = (selectedNonStandardLevelNs) => {
        const updatedSelectedNonStandardLevelNs = selectedNonStandardLevelNs.map((item) => ({
            frameworkId: item.frameworkId,
            levelN_ItemId: item.levelN_ItemId,
            indicatorUUID: indicatorUUID,
            organizationUUID: loginData.loggedOrganizationUuid,
        }));
        setSelectedNonStandardLevelNs(updatedSelectedNonStandardLevelNs);
    }

    const indicatorTypes = [
        { value: 'QUANTITATIVE', label: t('Quantitative') },
        { value: 'QUALITATIVE', label: t('Qualitative') },
        { value: 'QUANTITATIVE_DIMENSIONAL', label: t('Quantitative Dimensional') },
    ];

    return (
        <B2BPopUpForm
            title={(indicator && indicator.indicatorName) || 'New Custom Indicator'}
            open={open}
            onClose={onClose}
            className={'edit-custom-indicator b2b-light-01'}
            buttons={[
                {
                    label: t('Save'),
                    onClick: handleSaveIndicator
                },
                {
                    label: t('Cancel'),
                    onClick: onClose
                }
            ]}
        >
            <div className='edit-custom-indicator-common-fields'>
                <EditText label={t('Name')}
                    object={currentIndicator}
                    objectField={'indicatorName'}
                    placeholder={t('Indicator name')}
                    setObjectField={setCurrentIndicator}
                    className={'edit-custom-indicator-field-'}
                />
                {generateSelectField(indicatorTypes, 
                    {
                        label: t('Indicator Type'),
                        value: currentIndicator.indicatorType,
                        onChange: (event) => setCurrentIndicator({ ...currentIndicator, indicatorType: event.target.value }),
                        className: 'edit-custom-indicator-type',
                        disabled: currentIndicator.indicatorUUID
                    }
                )}
                {/* <BooleanToggleField label={t('Enabled')} field='indicatorEnabled' object={currentIndicator} setObject={setCurrentIndicator} /> */}
                <EditText label={t('Description')}
                    object={currentIndicator}
                    objectField={'description'}
                    placeholder={t('Indicator description...')}
                    setObjectField={setCurrentIndicator}
                    multiline={true}
                    rows={5}
                    className={'edit-custom-indicator-field-'}
                />
                <div className='edit-custom-indicator-specific-fields'>
                    <IndicatorsFieldsFactory indicator={currentIndicator} setIndicator={setCurrentIndicator} indicatorType={currentIndicator.indicatorType} />
                </div>
            </div>
            <Banner sectionTittle={t('Relation to frameworks')} message={t('Agenda 2030')} underline={false} headingLevel={'h3'} /> 
            <p>{t('Select the SDGs that are related to this indicator')}</p>
            <NonStandardLevelNs frameworkSuite={'generic'} frameworkId={2}
                selectedNonStandardLevelNs={selectedNonStandardLevelNs}
                onUpdate={handleUpdatedNonStandardLevelNs} />
        </B2BPopUpForm>    
    );
}

export default EditCustomIndicatorModal;